import { Sidebar } from '../modules/Sidebar';

const setViewportHeight = () => {
    document.body?.style.setProperty('--vh', `${window.innerHeight}px`);
};

document.addEventListener('DOMContentLoaded', setViewportHeight);
window.addEventListener('resize', setViewportHeight);
window.addEventListener('orientationchange', setViewportHeight);

class Header {
    constructor() {
        this.root = document.querySelector('[js-nav]');
        if (!this.root) return;

        this.init();
    }

    init() {
        this.hamburger = this.root.querySelector('[js-hamburger]');
        this.menuElementsTriggers = this.root.querySelectorAll(
            '[js-trigger-menu-element]'
        );
        this.hamburgerSpans = this.hamburger?.querySelectorAll('span');
        this.menuElements = document.querySelectorAll('[js-menu-element]');
        this.megamenuWrapper = document.querySelector('[js-megamenu-wrapper]');
        this.megamenuElements = document.querySelectorAll(
            '[js-megamenu-element]'
        );
        this.megamenu = document.querySelector('[js-header-megamenu]');
        this.overlay = document.querySelector('[js-overlay]');
        this.submenus = document.querySelectorAll('[js-submenu-key]');
        this.mobileBackButtons = document.querySelectorAll(
            '[js-mobile-back-arrow]'
        );

        this.activeSubmenuClass = '-active';
        this.hoverTimeout = null;
        this.hoverTime = 200;
        this.isMegamenuOpen = false;
        this.resizeObserver = null;

        this.events();
        this.onWindowScroll();
        this.updateLayoutVariables();
        this.observeNavbarHeight();
    }

    events() {
        if (this.menuElementsTriggers.length) {
            this.menuElementsTriggers.forEach(trigger => {
                trigger.addEventListener(
                    'click',
                    this.onTriggerClick.bind(this)
                );
            });
        }

        if (this.megamenuElements.length) {
            this.megamenuElements.forEach(element => {
                element.addEventListener(
                    'mouseenter',
                    this.onMouseEnter.bind(this)
                );
                element.addEventListener(
                    'mouseleave',
                    this.onMouseLeave.bind(this)
                );
                element.addEventListener(
                    'click',
                    this.onMenuElementClick.bind(this)
                );
            });
        }

        if (this.overlay) {
            this.overlay.addEventListener(
                'click',
                this.closeMenuElements.bind(this)
            );
        }

        if (this.mobileBackButtons.length) {
            this.mobileBackButtons.forEach(element => {
                element.addEventListener(
                    'click',
                    this.onMobileBackButtonClick.bind(this)
                );
            });
        }

        window.addEventListener('scroll', this.onWindowScroll.bind(this));
        window.addEventListener(
            'resize',
            this.updateLayoutVariables.bind(this)
        );
        window.addEventListener(
            'orientationchange',
            this.updateLayoutVariables.bind(this)
        );
    }

    updateLayoutVariables() {
        setViewportHeight();
        document.body?.style.setProperty(
            '--navbar-height',
            `${this.root.offsetHeight}px`
        );
    }

    observeNavbarHeight() {
        if (typeof ResizeObserver === 'undefined') return;

        this.resizeObserver = new ResizeObserver(() => {
            this.updateLayoutVariables();
        });

        this.resizeObserver.observe(this.root);
    }

    onMobileBackButtonClick() {
        this.submenus.forEach(element => {
            element.classList.remove('-active');
            this.resetSubmenuRows(element);
        });
        this.megamenuElements.forEach(element => {
            element.classList.remove(this.activeSubmenuClass);
        });
        this.megamenu.classList.remove('-mobile-hidden');
    }

    onMenuElementClick(e) {
        if (window.innerWidth >= 1024) return;
        this.onMenuElementHover(e);
    }

    onMouseEnter(event) {
        if (window.innerWidth < 1024) return;
        if (this.hoverTimeout) {
            clearTimeout(this.hoverTimeout);
        }

        this.hoverTimeout = setTimeout(() => {
            this.onMenuElementHover(event);
        }, this.hoverTime);
    }

    onMenuElementHover(e) {
        const { target: eTarget } = e;
        const target = eTarget.closest('[js-megamenu-element]');
        const hasSubmenu = target.querySelector('[js-element-submenu]');
        if (target.classList.contains(this.activeSubmenuClass)) return;
        this.megamenu.classList.remove('-closed');
        this.megamenuElements.forEach(element => {
            element.classList.remove(this.activeSubmenuClass);
        });

        this.submenus.forEach(item => {
            item.classList.remove('-active');
            this.resetSubmenuRows(item);
        });

        if (hasSubmenu) {
            this.megamenu.classList.add('-mobile-hidden');
            this.megamenu.classList.add('-closed');

            const targetSubmenu = hasSubmenu.getAttribute('js-element-submenu');

            const submenuToOpen = Array.from(this.submenus).find(
                item => item.getAttribute('js-submenu-key') === targetSubmenu
            );
            if (submenuToOpen) {
                submenuToOpen.classList.add('-active');
                this.animateSubmenuRows(submenuToOpen);
            }
        }

        target.classList.add(this.activeSubmenuClass);

        const isFirstOpen = target.closest('.-first-open');

        if (isFirstOpen) {
            isFirstOpen.classList.remove('-first-open');
        }
    }

    onMouseLeave() {
        if (window.innerWidth < 1024) return;
        if (this.hoverTimeout) {
            clearTimeout(this.hoverTimeout);
            this.hoverTimeout = null;
        }
    }

    closeMenuElements(triggerElement) {
        const wasMegamenuOpen = this.isMegamenuOpen;

        if (
            (triggerElement === 'megamenu' ||
                typeof triggerElement !== 'string') &&
            document.querySelector('[js-menu-element="megamenu"].-active')
        ) {
            this.handleBurgerClick(false);
        }
        this.root.classList.remove('expanded');
        this.megamenuWrapper.classList.remove('-active');
        this.megamenu.classList.remove('-closed');
        this.menuElementsTriggers.forEach(element => {
            element.classList.remove('active');
        });
        this.menuElements.forEach(element => {
            element.classList.remove('-active');
            element.classList.add('-first-open');
        });
        this.submenus.forEach(item => {
            item.classList.remove('-active');
            this.resetSubmenuRows(item);
        });
        this.menuElementsTriggers.forEach(element => {
            element.classList.remove('-blocked');
        });
        this.megamenu.classList.remove('-mobile-hidden');
        this.megamenuWrapper.classList.remove('-search-active');
        this.root?.classList.remove('search-open');

        if (wasMegamenuOpen) {
            Sidebar.unlockPageScroll();
            this.isMegamenuOpen = false;
        }
    }

    resetSubmenuRows(submenu) {
        submenu?.querySelectorAll('[js-submenu-row]').forEach(row => {
            row.getAnimations().forEach(animation => animation.cancel());
            row.style.opacity = '';
            row.style.transform = '';
        });
    }

    animateSubmenuRows(submenu) {
        const rows = submenu?.querySelectorAll('[js-submenu-row]');

        if (!rows?.length) return;

        rows.forEach((row, index) => {
            row.getAnimations().forEach(animation => animation.cancel());
            row.animate(
                [
                    {
                        opacity: 0,
                        transform: 'translateX(-16px)',
                    },
                    {
                        opacity: 1,
                        transform: 'translateX(0)',
                    },
                ],
                {
                    duration: 300,
                    delay: index * 100,
                    easing: 'ease-out',
                    fill: 'backwards',
                }
            );
        });
    }

    onTriggerClick(e) {
        const { currentTarget } = e;
        const triggerElement = currentTarget.getAttribute(
            'js-trigger-menu-element'
        );
        if (!triggerElement.length) return;

        if (
            triggerElement !== 'megamenu' &&
            document.querySelector('[js-menu-element="megamenu"].-active')
        ) {
            this.handleBurgerClick(false);
        }

        const menuElement = Array.from(this.menuElements).find(
            element =>
                element.getAttribute('js-menu-element') === triggerElement
        );
        const isActive = currentTarget.classList.contains('active');

        this.closeMenuElements(triggerElement);
        menuElement?.classList.remove('-active');

        if (isActive) {
            if (triggerElement === 'search') {
                this.megamenuWrapper.classList.remove('-search-active');
            }

            menuElement?.classList.add('-first-open');
            return;
        }

        if (triggerElement === 'search') {
            this.megamenuWrapper.classList.add('-search-active');
        } else {
            this.megamenuWrapper.classList.remove('-search-active');
        }
        this.menuElementsTriggers.forEach(element => {
            element.classList.add('-blocked');
        });
        currentTarget.classList.remove('-blocked');

        this.root.classList.add('expanded');
        currentTarget.classList.add('active');
        this.megamenuWrapper.classList.add('-active');
        menuElement?.classList.add('-active');
        if (triggerElement === 'megamenu') {
            Sidebar.lockPageScroll();
            this.isMegamenuOpen = true;
            this.handleBurgerClick(true);
        }
    }

    onWindowScroll() {
        if (window.scrollY > 50) {
            this.root.classList.add('active');
        } else {
            this.root.classList.remove('active');
        }
    }

    handleBurgerClick(isOpened = true) {
        this.hamburger.classList.add('pointer-events-none');
        this.megamenuElements.forEach(element => {
            element.classList.remove('-active');
        });
        if (isOpened) {
            // zamknięcie - schowanie trzech
            this.hamburgerSpans.forEach((element, index) => {
                const reversedIndex = this.hamburgerSpans.length - 1 - index;
                setTimeout(() => {
                    element.classList.add('scale-x-0');
                }, reversedIndex * 30);

                setTimeout(() => {
                    element.classList.add('absolute');
                    element.classList.remove('origin-left');
                    if (index === 0) {
                        element.classList.add('rotate-45');
                    } else if (index === 1) {
                        element.classList.add('-rotate-45');
                    } else {
                        element.classList.add('hidden');
                    }
                    setTimeout(() => {
                        element.classList.remove('scale-x-0');
                        this.hamburger.classList.remove('pointer-events-none');
                    }, 50);
                }, 300);
            });
        } else {
            this.hamburgerSpans.forEach((element, index) => {
                element.classList.add('scale-x-0');

                setTimeout(() => {
                    element.classList.add('origin-left');
                    element.classList.remove('absolute');
                    element.classList.remove('hidden');
                    element.classList.remove('rotate-45');
                    element.classList.remove('-rotate-45');
                    setTimeout(() => {
                        this.hamburgerSpans.forEach((element, index) => {
                            setTimeout(() => {
                                element.classList.remove('scale-x-0');
                                this.hamburger.classList.remove(
                                    'pointer-events-none'
                                );
                            }, index * 30);
                        });
                    }, 90);
                }, 200);
            });
        }
    }
}

new Header();
